import { useEffect, useRef } from 'react';

import { FailureKind } from '@sb/routine-runner';
import {
  useIsRobotConnected,
  useRobotFailureState,
  useToast,
} from '@sbrc/hooks';

// Surfaces every live-robot failure/E-Stop as a toast, mounted globally so it
// fires even outside the play flow (e.g. an idle E-Stop) — which usePlayRobot missed.
export default function RobotFailureToastBridge() {
  const isConnected = useIsRobotConnected({});
  const failureState = useRobotFailureState({});
  const { setToast } = useToast();

  // A deliberate power-off is not an error — the power-off flow gives its
  // own feedback, so there is nothing to toast.
  const failureReason =
    failureState?.failure.kind === FailureKind.PoweredOff
      ? null
      : failureState?.failureReason ?? null;

  const prevReasonRef = useRef<string | null>(null);

  useEffect(() => {
    // While disconnected, failureState reads null regardless of the real robot
    // state; skip so a reconnect blip during a sticky failure doesn't re-toast.
    if (!isConnected) return;

    if (failureReason && failureReason !== prevReasonRef.current) {
      setToast({ kind: 'error', message: failureReason });
    }

    prevReasonRef.current = failureReason;
  }, [failureReason, isConnected, setToast]);

  return null;
}
